import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import {
  IntegrationConnectionStatus,
  NotificationType,
} from "@markit/common.types";
import { API } from "../../../../API";
import { Colors } from "../../../../utils/colors";
import {
  accountActions,
  getAccountState,
} from "../../../../redux/slices/accountSlice";
import { GetNumericDate } from "../../../../utils/GetTime";
import IntegrationPanelContainer from "./IntegrationPanelContainer";
import ConfirmActionModal from "../../../Containers/ConfirmPopups/ConfirmActionModal";
import AlertContainer from "../../../Containers/AlertContainer";
import RectangleButton from "../../../Buttons/RectangleButton";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import GoogleCalendarIcon from "../../../IntegrationIcons/GoogleCalendarIcon";
import { showNotificationBanner } from "../../../../utils/notificationUtils";

const googleCalendarDescription =
  "Auto-add your Markit events to your Google Calendar and send calendar invites to guests.";

type AlertText = { header: string; subHeader: string };

const CONNECT_FAILED_ALERT: AlertText = {
  header: "Something went wrong connecting Google Calendar.",
  subHeader: "Please try again.",
};

export const GoogleCalendarPanelSection = () => {
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [loadingDisconnect, setLoadingDisconnect] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [alertText, setAlertText] = useState<AlertText>({
    header: "",
    subHeader: "",
  });

  const { googleCalendarInfo } = accountData;
  const isConnected =
    googleCalendarInfo.status === IntegrationConnectionStatus.CONNECTED;
  const needsReconnect =
    googleCalendarInfo.status === IntegrationConnectionStatus.ERROR;

  const connectOnPress = useCallback(async () => {
    setLoadingConnect(true);
    try {
      const { url } = await API.googleCalendarApi.authorizeGoogleCalendar({});
      window.location.assign(url);
    } catch (error) {
      console.error(error);
      setLoadingConnect(false);
      setAlertText(CONNECT_FAILED_ALERT);
    }
  }, []);

  const disconnectOnPress = useCallback(async () => {
    setLoadingDisconnect(true);
    try {
      await API.googleCalendarApi.disconnectGoogleCalendar({});
      dispatch(
        accountActions.updateAccount({
          googleCalendarInfo: {
            tokenHash: "",
            googleEmail: "",
            status: IntegrationConnectionStatus.NOT_CONNECTED,
            connectedOn: "",
          },
        })
      );
      showNotificationBanner(
        dispatch,
        "Disconnected",
        NotificationType.NEGATIVE
      );
    } catch (error) {
      console.error(error);
      setAlertText({
        header: "Something went wrong disconnecting Google Calendar.",
        subHeader: "Please try again.",
      });
    }
    setLoadingDisconnect(false);
    setShowDisconnectModal(false);
  }, [dispatch]);

  return (
    <>
      <IntegrationPanelContainer
        isConnected={isConnected || needsReconnect}
        connectedContent={
          <div
            className="ColumnNormal"
            style={{ height: "100%", width: "100%" }}
          >
            <div className="AlignedRow" style={{ gap: 14, padding: 14 }}>
              <GoogleCalendarIcon size={34} padding={10} />
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <div className="AlignedRow" style={{ gap: 10 }}>
                  <span className="sectionTitle">Google Calendar</span>
                  <span
                    className="bodySubtext"
                    style={needsReconnect ? { color: Colors.RED3 } : undefined}
                  >
                    {needsReconnect ? "Reconnect needed" : "Connected"}
                  </span>
                </div>
                <span className="smallBodySubtext">
                  {googleCalendarDescription}
                </span>
              </div>
            </div>
            <HorizontalDivider />
            <div
              className="ColumnNormal"
              style={{
                padding: 14,
                flexGrow: 1,
                justifyContent: "space-between",
              }}
            >
              <div className="ColumnNormal" style={{ gap: 10 }}>
                {needsReconnect ? (
                  <span
                    className="smallBodySubtext"
                    style={{ color: Colors.RED3 }}
                  >
                    Markit no longer has access to your calendar. Reconnect to
                    keep adding your events.
                  </span>
                ) : null}
                <span className="smallBodySubtext">
                  Account: {googleCalendarInfo.googleEmail}
                </span>
                <span className="smallBodySubtext">
                  Connected On: {GetNumericDate(googleCalendarInfo.connectedOn)}
                </span>
              </div>
              <div className="AlignedRow" style={{ gap: 10 }}>
                <RectangleButton
                  buttonLabel={
                    <span style={{ fontSize: 14, fontWeight: 400 }}>
                      Disconnect
                    </span>
                  }
                  onPress={() => setShowDisconnectModal(true)}
                  altColor={Colors.WHITE1}
                  altTextColor={Colors.RED3}
                  altPaddingHorz={14}
                  altPaddingVert={10}
                  altBorderRadius={8}
                  width={106}
                  containerStyles={{ border: `0.5px solid ${Colors.GRAY2}` }}
                />
                {needsReconnect ? (
                  <RectangleButton
                    buttonLabel={
                      <span style={{ fontSize: 14, fontWeight: 400 }}>
                        Reconnect
                      </span>
                    }
                    onPress={connectOnPress}
                    altColor={Colors.BLACK}
                    altTextColor={Colors.WHITE}
                    altPaddingHorz={14}
                    altPaddingVert={10}
                    altBorderRadius={8}
                    disabled={loadingConnect}
                    loading={loadingConnect}
                  />
                ) : null}
              </div>
            </div>
          </div>
        }
        title="Google Calendar Integration"
        description={googleCalendarDescription}
        actionLabel="Connect"
        onActionPress={() => setShowConnectModal(true)}
        actionLoading={loadingConnect}
        icon={<GoogleCalendarIcon size={57} padding={22} borderRadius={15} />}
      />
      <ConfirmActionModal
        heading="Connect to Google Calendar"
        subtext="We'll take you to Google to approve access. Markit only asks to see, create, change, and delete events on calendars you own."
        confirmButtonText="Connect"
        icon={<Icon icon="logos:google-calendar" height={35} />}
        hideModal={!showConnectModal}
        setIsVisible={setShowConnectModal}
        confirmOnPress={() => {
          setShowConnectModal(false);
          connectOnPress();
        }}
      />
      <ConfirmActionModal
        heading="Are you sure you want to disconnect your Google Calendar from Markit?"
        subtext="Markit will stop adding events to your calendar. You can reconnect at any time."
        confirmButtonText="Disconnect"
        hideModal={!showDisconnectModal}
        setIsVisible={setShowDisconnectModal}
        confirmOnPress={disconnectOnPress}
        loadingConfirm={loadingDisconnect}
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
    </>
  );
};
